<template>
  <UiDialog
    class="LinkButton"
    :open.sync="isOpen"
    :close-scrim="false"
  >
    <template #trigger>
      <UiIcon
        :title="$t('LinkButton.title')"
        value="mdi:link"
        class="ToolbarButton"
        :class="{ 'is-active': isActive }"
      ></UiIcon>
    </template>

    <template #contents>
        <h1>{{ $t('LinkButton.title') }}</h1> <br>

        <div style="gap: 15px; display: flex; align-items: center;">
          <UiInput
            type="text"
            v-model="link.href"
            :label="$t('LinkButton.link')"
          ></UiInput>

          <UiIcon style="cursor: pointer;" value="mdi:folder-search-outline" @click="openFileInput" />
        </div>

        <UiInput
          v-if="!isImage.src"
          type="text"
          v-model="link.text"
          :label="$t('LinkButton.textToDisplay')"
        ></UiInput>

        <UiInput
          :label="$t('LinkButton.OpenInNewWindow')"
          type="checkbox"
          v-model="link.target"
        ></UiInput>

        <FilePicker
          v-if="isOpenFolder"
          @select="selectImage"
        ></FilePicker>
    </template>

    <template #footer>
      <button
        type="button"
        class="ui-native --main"
        @click="onAccept"
      >{{ $t('LinkButton.Accept') }}</button>
      <button
        type="button"
        class="ui-native --cancel"
        @click="onCancel"
      >{{ $t('LinkButton.Cancel') }}</button>
    </template>
  </UiDialog>
</template>

<script>
import useI18n from '@/modules/i18n/mixins/useI18n.js';
import { UiIcon, UiDialog, UiInput } from '@/modules/ui/components';

import FilePicker from './FilePicker.vue';

export default {
  components: {
    UiIcon,
    UiDialog,
    UiInput,
    FilePicker
  },

  mixins: [useI18n],

  props: {
    editor: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isOpen: false,

      link: {
        href: '',
        target: '',
        text: '',
      },
      isOpenFolder: false
    };
  },

  methods: {
    onAccept() {
      if (!this.link.href?.trim?.()) {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
        this.isOpen = false;
        return;
      }

      const { from, to } = this.editor.state.selection;
      const transaction = this.editor.state.tr;
      const existingLink = this.editor.getAttributes('link');

      if (existingLink.href) {
        transaction.removeMark(from, to, this.editor.schema.marks.link);
      }

      if (this.link.text) {
        transaction.insertText(this.link.text, from, to);
      }

      this.link.target = this.link.target ? '_blank' : '';

      if(this.isImage.src) {
        this.editor.chain().focus().setImage({ src: this.isImage.src, width: this.isImage.width, height: this.isImage.height, style: this.isImage.style }).selectParentNode().setLink(this.link).run();
        this.isOpen = false;
        return;
      }

      const newTo = from + this.link.text.length;
      transaction.addMark(from, newTo, this.editor.schema.marks.link.create(this.link));

      this.editor.view.dispatch(transaction);

      this.isOpen = false;
    },

    onCancel() {
      this.isOpen = false;
    },

    selectImage(image) {
      this.setUrl(image.url);
      this.isOpenFolder = false;
    },

    setUrl(url) {
      if (!url) {
        return;
      }

      this.link.href = url;
      this.link.text = this.link.text ? this.link.text : url;
    },

    openFileInput() {
      this.isOpenFolder = true;
    },
  },

  computed: {
    currentValue() {
      const currentLink = this.editor.getAttributes('link');

      const selection = this.editor.state.selection;
      const selectedText =
        selection && selection.$from !== selection.$to
          ? selection
              .content()
              .content.textBetween(0, selection.content().content.size, '\n')
          : '';

      return {
        ...currentLink,
        text: selectedText,
      };
    },

    isActive() {
      return this.editor.isActive('link');
    },

    isImage() {
      return this.editor.getAttributes("image");
    }
  },

  watch: {
    currentValue(newValue) {
      this.link = newValue;
    },
  },

  i18n: {
    en: {
        'LinkButton.link': 'Link',
        'LinkButton.Cancel': 'Cancel',
        'LinkButton.Accept': 'Accept',
        'LinkButton.title': 'Insert/edit link',
        'LinkButton.textToDisplay': 'Text to display',
        'LinkButton.OpenInNewWindow': 'Open in new window',
      },

      es: {
        'LinkButton.link': 'Enlace',
        'LinkButton.Cancel': 'Cancelar',
        'LinkButton.Accept': 'Aceptar',
        'LinkButton.title': 'Insertar/editar enlace',
        'LinkButton.textToDisplay': 'Texto a mostrar',
        'LinkButton.OpenInNewWindow': 'Abrir nueva ventana',
      },

      de: {
        'LinkButton.link': 'Link',
        'LinkButton.Cancel': 'Abbrechen',
        'LinkButton.Accept': 'Akzeptieren',
        'LinkButton.title': 'Link einfügen/bearbeiten',
        'LinkButton.textToDisplay': 'Anzuzeigender Text',
        'LinkButton.OpenInNewWindow': 'In einem neuen Fenster öffnen',
      },
  },
};
</script>

<style lang="scss">
.LinkButton .ui-dialog-container {
  position: static;
  max-width: 600px !important;
}
.LinkButton .ui-field {
  width: 100%;
  margin-bottom: 10px;
  input {
    width: 100%;
  }
}
</style>